import { serializeFilters, toAnalyticsParams } from './pmAnalyticsFilters';

// PM Analitik Faz 2 Plan 4 (spec §7, §19) — E02 Süreç Takibi'nin URL sözleşmesi. Ortak süzgeçler (dönem, organizasyon, …)
// pmAnalyticsFilters'ın anahtarlarında; bu modül yalnız E02'ye özgü durumu taşır: sekme, M03 kartının aşaması ve Görevler sekmesinin
// süzgeçleri. İK Operasyon Panosu'ndan ve Özet sekmesinden Görevler'e giden her bağlantı processHealthHref'ten kurulur (§19/4) —
// böylece tıklanan sayı ile açılan liste aynı kümeyi tanımlar (görev listesi her zaman yalnız zorunlu görevler, §19/2).

export const PROCESS_HEALTH_PATH = '/performance/process-health';
export const TAB_SUMMARY = 'summary';
export const TAB_TASKS = 'tasks';

export const TASK_STATUSES = ['not_opened', 'open', 'draft', 'submitted', 'returned', 'done', 'exempt', 'cancelled'];
// pm.tasksInProgress — açık durumdaki ve gecikmemiş zorunlu görev (spec §3.1).
export const IN_PROGRESS_STATUSES = ['open', 'draft', 'submitted', 'returned'];
export const ASSIGNEE_ROLES = ['employee', 'manager', 'hr', 'rater'];
export const ASSIGNEE_KINDS = ['employee', 'hr_pool', 'external'];
export const OVERDUE_BANDS = ['1_3', '4_7', '8_14', '15_plus'];
export const OVERDUE_VALUES = ['true', 'false'];
export const DEFAULT_PILEUP_WINDOW_DAYS = 7;
// Dönemin aşama sırası (PeriodTaskCalculator.StageOrder) — URL doğrulaması ve süzgeç seçenekleri.
export const STAGE_ORDER = [
  'goal_setting', 'goal_evaluation', 'self_review', 'manager_review', 'nomination', 'feedback_360', 'calibration', 'final_result', 'result_ack',
];
// Görev listesine bağlanan süreç kuralları (E02 AI alanı); diğer bulgular (halef boşluğu, potansiyel eksik, veri tamlığı) görev kümesi değildir.
export const PROCESS_RULES = ['pm.overdue.concentration', 'pm.stage.slowdown', 'pm.due.pileup'];

// Ortak şeridin anahtarları (period, dept, loc, mgr, sub, title, group, pstatus, org, cmp) ile çakışmaz.
const K = {
  tab: 'tab',
  m03: 'm03',
  stage: 'stage',
  statuses: 'status',
  overdue: 'overdue',
  assigneeEmployeeId: 'assignee',
  assigneeKind: 'akind',
  roles: 'role',
  dueFrom: 'dueFrom',
  dueTo: 'dueTo',
  bands: 'band',
  dueWithinDays: 'within',
};

export const EMPTY_TASK_FILTERS = Object.freeze({
  stage: null,
  statuses: [],
  overdue: null,              // 'true' | 'false' | null (hepsi)
  assigneeEmployeeId: null,
  assigneeKind: null,         // employee | hr_pool | external
  roles: [],
  dueFrom: null,              // 'YYYY-MM-DD' (kiracı günü; sunucu yorumlar)
  dueTo: null,
  bands: [],
  dueWithinDays: null,        // 1–30
});

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

// Bağlantı üretilebilir anahtar: boş, "Diğer" kovası ve Guid.Empty (departmansız katılımcılar) tıklanabilir bir kapsam değildir.
export const isRealKey = (k) => !!k && k !== EMPTY_GUID && k !== 'other';

const list = (v) => (v ? v.split(',').map((x) => x.trim()).filter(Boolean) : []);
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const guid = (v) => (v && GUID.test(v) ? v : null);
const stage = (v) => (STAGE_ORDER.includes(v) ? v : null);
// Takvimde var olan gün (2026-02-30 düşer): elle düzenlenmiş ya da bayat bağlantı sunucuda 400 + tost üretmesin.
const day = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v || '');
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3]) ? m[0] : null;
};
const withinDays = (v) => {
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 && n <= 30 ? n : null;
};

export function parseTaskFilters(p) {
  const overdue = p.get(K.overdue);
  const kind = p.get(K.assigneeKind);
  return {
    stage: stage(p.get(K.stage)),
    statuses: list(p.get(K.statuses)).filter((s) => TASK_STATUSES.includes(s)),
    overdue: OVERDUE_VALUES.includes(overdue) ? overdue : null,
    assigneeEmployeeId: guid(p.get(K.assigneeEmployeeId)),
    assigneeKind: ASSIGNEE_KINDS.includes(kind) ? kind : null,
    roles: list(p.get(K.roles)).filter((r) => ASSIGNEE_ROLES.includes(r)),
    dueFrom: day(p.get(K.dueFrom)),
    dueTo: day(p.get(K.dueTo)),
    bands: list(p.get(K.bands)).filter((b) => OVERDUE_BANDS.includes(b)),
    dueWithinDays: withinDays(p.get(K.dueWithinDays)),
  };
}

export function parseProcessHealth(search) {
  const p = new URLSearchParams(search || '');
  return {
    tab: p.get(K.tab) === TAB_TASKS ? TAB_TASKS : TAB_SUMMARY,
    m03Stage: stage(p.get(K.m03)),
    taskFilters: parseTaskFilters(p),
  };
}

// Mevcut sorgu dizesindeki ortak süzgeç anahtarları korunur; E02 anahtarları yeniden yazılır, varsayılanlar yazılmaz.
export function serializeProcessHealth({ tab = TAB_SUMMARY, m03Stage = null, taskFilters = EMPTY_TASK_FILTERS } = {}, baseSearch = '') {
  const p = new URLSearchParams(baseSearch || '');
  Object.values(K).forEach((k) => p.delete(k));
  const f = { ...EMPTY_TASK_FILTERS, ...taskFilters };
  if (tab === TAB_TASKS) p.set(K.tab, TAB_TASKS);
  if (m03Stage) p.set(K.m03, m03Stage);
  if (f.stage) p.set(K.stage, f.stage);
  if (f.statuses.length) p.set(K.statuses, f.statuses.join(','));
  if (f.overdue) p.set(K.overdue, f.overdue);
  if (f.assigneeEmployeeId) p.set(K.assigneeEmployeeId, f.assigneeEmployeeId);
  if (f.assigneeKind) p.set(K.assigneeKind, f.assigneeKind);
  if (f.roles.length) p.set(K.roles, f.roles.join(','));
  if (f.dueFrom) p.set(K.dueFrom, f.dueFrom);
  if (f.dueTo) p.set(K.dueTo, f.dueTo);
  if (f.bands.length) p.set(K.bands, f.bands.join(','));
  if (f.dueWithinDays) p.set(K.dueWithinDays, String(f.dueWithinDays));
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * E02'ye bağlantı: mevcut ortak süzgeçler + hedef. `departmentIds` verilirse ortak organizasyon süzgecinin YERİNE geçer (birim
 * satırı/çubuğu tıklaması o birimi açar). `task` Görevler sekmesinin süzgeçleridir.
 */
export function processHealthHref(filters, { tab = TAB_SUMMARY, m03Stage = null, departmentIds, task = {} } = {}) {
  const shared = serializeFilters(departmentIds ? { ...filters, departmentIds } : filters);
  return `${PROCESS_HEALTH_PATH}${serializeProcessHealth({ tab, m03Stage, taskFilters: { ...EMPTY_TASK_FILTERS, ...task } }, shared)}`;
}

// Görev ucunun parametreleri (PerformanceAnalyticsController.GetTasks izin listesi). Liste her zaman yalnız zorunlu görevler (§19/2).
export function taskFiltersToParams(taskFilters) {
  const f = { ...EMPTY_TASK_FILTERS, ...taskFilters };
  const params = { mandatory: 'true' };
  if (f.stage) params['stage[eq]'] = f.stage;
  if (f.statuses.length) params['status[in]'] = f.statuses.join(',');
  if (f.overdue) params.overdue = f.overdue;
  if (f.assigneeEmployeeId) params['assigneeEmployeeId[eq]'] = f.assigneeEmployeeId;
  if (f.assigneeKind) params['assigneeKind[eq]'] = f.assigneeKind;
  if (f.roles.length) params['assigneeRole[in]'] = f.roles.join(',');
  if (f.dueFrom) params['dueAt[gte]'] = f.dueFrom;
  if (f.dueTo) params['dueAt[lte]'] = f.dueTo;
  if (f.bands.length) params['overdueBand[in]'] = f.bands.join(',');
  if (f.dueWithinDays) params.dueWithinDays = String(f.dueWithinDays);
  return params;
}

// Etkin görev süzgeçleri — şeridin etiket satırında silinebilir etiketler.
export function activeTaskFilterKeys(taskFilters) {
  const f = { ...EMPTY_TASK_FILTERS, ...taskFilters };
  return Object.keys(EMPTY_TASK_FILTERS).filter((k) => (Array.isArray(f[k]) ? f[k].length > 0 : f[k] != null));
}

export function clearTaskFilter(taskFilters, key) {
  return { ...EMPTY_TASK_FILTERS, ...taskFilters, [key]: EMPTY_TASK_FILTERS[key] };
}

/**
 * Geciken Sorumlular "Hatırlat" ölçütü (§19/11): görülen satırla aynı küme — ortak süzgeçler + bu sorumlunun geciken zorunlu görevleri.
 * Dönem istek gövdesinde ayrıca gider; ölçüt periodId taşımaz (PmReminderRules.CommonKeys).
 */
export function assigneeReminderCriteria(filters, assigneeEmployeeId) {
  const shared = { ...toAnalyticsParams(filters) };
  delete shared['periodId[eq]'];
  return { ...shared, 'assigneeEmployeeId[eq]': assigneeEmployeeId, overdue: 'true', mandatory: 'true' };
}

/**
 * Durum diliminin Görevler hedefi — İK panosunun aşama dağılımı, Özet'in aşama × durum matrisi ve birim çubukları AYNI kuralı kullanır
 * (sayı = liste, §19/4): Devam Ediyor/Bekliyor = pm.tasksInProgress = açık durumlar VE gecikmemiş; Gecikti = M04.
 */
export function statusTarget(segment) {
  switch (segment) {
    case 'done': return { statuses: ['done'] };
    case 'inProgress': return { statuses: IN_PROGRESS_STATUSES, overdue: 'false' };
    case 'overdue': return { overdue: 'true' };
    case 'exempt': return { statuses: ['exempt'] };
    case 'notOpened': return { statuses: ['not_opened'] };
    default: return {};
  }
}

/**
 * Bulgu kartının "Görevleri Gör" hedefi (§19/10): kapsamı ve türü listeye süzgeç olarak taşınır. Süreç kuralı değilse null — kartın
 * sayısı ile açılacak liste farklı kümeler olurdu (son inceleme, Önemli 1).
 */
export function insightTaskTarget(insight) {
  if (!PROCESS_RULES.includes(insight?.ruleCode)) return null;
  const task = {};
  let departmentIds;
  if (insight?.scopeType === 'department' && isRealKey(insight.scopeId)) departmentIds = [insight.scopeId];
  if (insight?.ruleCode === 'pm.overdue.concentration') task.overdue = 'true';
  if (insight?.ruleCode === 'pm.due.pileup') {
    task.dueWithinDays = withinDays(insight.evidence?.thresholds?.pileupWindowDays) || DEFAULT_PILEUP_WINDOW_DAYS;
  }
  if (insight?.ruleCode === 'pm.stage.slowdown') task.stage = insight.evidence?.items?.[0]?.stageKey || null;
  return { tab: TAB_TASKS, departmentIds, task };
}
